import { matchRouteTemplate, type RouteTemplateIndex } from './route-template-match';

/**
 * Collapse high-cardinality path segments to a stable `:id` placeholder so a RUM
 * view name is always a route template, never an interpolated path carrying a raw
 * member id. RUM view names must not embed interpolated routes or high-cardinality
 * ids (telemetry taxonomy Rule 1) and must never egress a raw member id (Rule 3);
 * the standard `beforeSend` scrubber can't catch an id embedded in a path string,
 * so it is collapsed here — at the provider seam — before it reaches the SDK.
 *
 * Conservative by design: only segments that are unambiguously identifiers (UUID,
 * all-digits, long hex, or an opaque token carrying a digit) are collapsed;
 * human-readable route slugs (`member-lookup`, `today_view`, `v1`) pass through
 * unchanged. Any query string / fragment is dropped (a query can carry ids and is
 * never part of a meaningful view name).
 */
export function templatizeRumViewName(path: string, pageTemplates?: RouteTemplateIndex): string {
  if (!path) return path;
  const pathname = path.split(/[?#]/, 1)[0] ?? '';

  // A registered page template is authoritative when one matches: it knows which
  // segments are identifiers regardless of what they contain, so it catches the
  // short ids the shape rules below miss (`member-123` is under the opaque-id
  // length floor and is neither UUID, all-digits, nor long hex).
  //
  // Only PAGE templates are consulted. A view name is a page path; matching it
  // against API route templates would be a category error.
  if (pageTemplates) {
    const template = matchRouteTemplate(pageTemplates, pathname);
    if (template !== null) return template;
  }

  // Fallback, unchanged. `pageTemplates` is optional and defaulted precisely so
  // this path stays byte-identical for existing callers: `apps/web` derives its
  // product-analytics `route_template` and `screen_id` from this function, so a
  // silent behaviour change here would move analytics identifiers, not just a
  // telemetry label.
  const templated = pathname
    .split('/')
    .map((seg) => (isIdSegment(seg) ? ':id' : seg))
    .join('/');
  return templated || '/';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ALL_DIGITS = /^\d+$/;
const LONG_HEX = /^[0-9a-f]{16,}$/i;
// Opaque id: ≥12 chars of an identifier alphabet that includes at least one digit
// (matches nanoid / base62 / ULID-style member ids without collapsing word slugs).
const OPAQUE_ID = /^(?=[A-Za-z0-9_-]*\d)[A-Za-z0-9_-]{12,}$/;

function isIdSegment(seg: string): boolean {
  return UUID.test(seg) || ALL_DIGITS.test(seg) || LONG_HEX.test(seg) || OPAQUE_ID.test(seg);
}
